/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { base64ToBlob } from "@src/appV2/lib/files";
import { useUploadShiftTimecardToS3 } from "@src/appV2/redesign/WorkerShifts/UnverifiedShifts/UploadTimesheet/useUploadShiftTimecardToS3";
import { useSubmitCaliforniaShiftTimeSheet } from "@src/appV2/Shifts/Shift/api/useSubmitCaliforniaShiftTimeSheet";
import { useSubmitShiftTimeSheet } from "@src/appV2/Shifts/Shift/api/useSubmitShiftTimeSheet";
import { type Shift } from "@src/appV2/Shifts/Shift/types";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import axios from "axios";
import { useCallback, useMemo } from "react";

import { type TimesheetStep } from "../../../ShiftDetails/Timekeeping/hooks/useTimesheetStep";
import { type UploadShiftTimecardToS3Response } from "../../../UnverifiedShifts/types";
import {
  createSubmitTimesheetFormSchema,
  type SubmitTimesheetFormValues,
} from "../schemas/submitTimesheetFormSchema";

// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line no-barrel-files/no-barrel-files
export type { SubmitTimesheetFormValues } from "../schemas/submitTimesheetFormSchema";

interface UseTimesheetSubmissionParams {
  shift?: Shift;
  isCaliforniaFacility: boolean;
  currentStep?: TimesheetStep;
  isQrCodeSubmission?: boolean;
  isUnverifiedTimesheetFlow?: boolean;
  isSkipWorkplaceSignatureFlow?: boolean;
  onSuccess: () => void;
  onError: (errorMessage: string) => void;
  actualClockInOut?: { start: string; end: string };
}

export function useTimesheetSubmission({
  shift,
  isCaliforniaFacility,
  currentStep,
  isQrCodeSubmission = false,
  isUnverifiedTimesheetFlow = false,
  isSkipWorkplaceSignatureFlow = false,
  onSuccess,
  onError,
  actualClockInOut,
}: UseTimesheetSubmissionParams) {
  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId: shift?.facility?.userId,
  });

  const { mutateAsync: uploadToS3, isLoading: isUploading } = useUploadShiftTimecardToS3({
    meta: {
      userErrorMessage: "Failed to upload signature. Please try again.",
    },
  });

  const { mutateAsync: submitTimesheet, isLoading: isSubmitting } = useSubmitShiftTimeSheet();
  const { mutateAsync: submitCaliforniaTimesheet, isLoading: isCaliforniaSubmitting } =
    useSubmitCaliforniaShiftTimeSheet();

  const formSchema = useMemo(
    () =>
      createSubmitTimesheetFormSchema({
        isCaliforniaFacility,
        currentStep,
        isQrCodeSubmission,
        isUnverifiedTimesheetFlow,
        isSkipWorkplaceSignatureFlow,
      }),
    [
      isCaliforniaFacility,
      currentStep,
      isQrCodeSubmission,
      isUnverifiedTimesheetFlow,
      isSkipWorkplaceSignatureFlow,
    ]
  );

  const submitCaliforniaTimecard = useCallback(
    async (data: SubmitTimesheetFormValues) => {
      if (
        !shift?._id ||
        !data.hcpName ||
        !data.hcpSignature ||
        !data.facilityEmployeeName ||
        !data.workerStation ||
        !data.workerNursingAssignment
      ) {
        onError("Missing required data for California submission");
        return;
      }

      // Get timecard either from data (QR code) or by uploading signature
      let facilityTimecard: UploadShiftTimecardToS3Response;
      if (data.timecard) {
        facilityTimecard = data.timecard;
      } else if (data.facilityEmployeeSignature) {
        const facilitySignatureBlob = await base64ToBlob(data.facilityEmployeeSignature);
        facilityTimecard = await uploadToS3({
          shiftId: shift._id,
          type: facilitySignatureBlob.type,
          fileBlob: facilitySignatureBlob,
        });
      } else {
        onError("Missing required data for California submission");
        return;
      }

      // Upload HCP signature to S3
      const hcpSignatureBlob = await base64ToBlob(data.hcpSignature);
      const hcpTimecard = await uploadToS3({
        shiftId: shift._id,
        type: hcpSignatureBlob.type,
        fileBlob: hcpSignatureBlob,
      });

      // Submit California timesheet with dual signatures
      await submitCaliforniaTimesheet({
        clockInOut: {
          start: data.clockInTime.toISOString(),
          end: data.clockOutTime.toISOString(),
        },
        lunchInOut:
          data.breakStartTime && data.breakEndTime
            ? {
                start: data.breakStartTime.toISOString(),
                end: data.breakEndTime.toISOString(),
              }
            : undefined,
        shiftId: shift._id,
        facilityEmployeeName: data.facilityEmployeeName,
        hcpName: data.hcpName,
        stationWingUnitFloor: data.workerStation,
        nursingServiceAssignment: data.workerNursingAssignment,
        hcpTimecard,
        facilityTimecard,
        mandatoryBreakDurationInMinutes,
        breakAuthorizationReason: data.breakAuthorizationReason,
        missedPunches: data.missedPunches,
        actualClockInOut: isDefined(actualClockInOut)
          ? { start: actualClockInOut.start, end: actualClockInOut.end }
          : undefined,
      });
    },
    [
      shift,
      uploadToS3,
      submitCaliforniaTimesheet,
      onError,
      mandatoryBreakDurationInMinutes,
      actualClockInOut,
    ]
  );

  const submitRegularTimecard = useCallback(
    async (data: SubmitTimesheetFormValues) => {
      if (!isDefined(shift?._id)) {
        return;
      }

      // Get timecard either from data (QR code) or by uploading signature
      let timecard: UploadShiftTimecardToS3Response;
      if (data.timecard) {
        timecard = data.timecard;
      } else if (data.facilityEmployeeSignature) {
        const signatureBlob = await base64ToBlob(data.facilityEmployeeSignature);
        timecard = await uploadToS3({
          shiftId: shift._id,
          type: signatureBlob.type,
          fileBlob: signatureBlob,
        });
      } else {
        onError("Missing required data for submission");
        return;
      }

      await submitTimesheet({
        clockInOut: {
          start: data.clockInTime.toISOString(),
          end: data.clockOutTime.toISOString(),
        },
        lunchInOut:
          data.breakStartTime && data.breakEndTime
            ? {
                start: data.breakStartTime.toISOString(),
                end: data.breakEndTime.toISOString(),
              }
            : undefined,
        shiftId: shift._id,
        signedBy: data.facilityEmployeeName,
        timecard,
        mandatoryBreakDurationInMinutes,
        breakAuthorizationReason: data.breakAuthorizationReason,
        missedPunches: data.missedPunches,
        actualClockInOut: isDefined(actualClockInOut)
          ? { start: actualClockInOut.start, end: actualClockInOut.end }
          : undefined,
      });
    },
    [shift, uploadToS3, submitTimesheet, mandatoryBreakDurationInMinutes, onError, actualClockInOut]
  );

  const submitCaliforniaTimecardWithoutHCFSignature = useCallback(
    async (data: SubmitTimesheetFormValues) => {
      if (!isDefined(shift?._id) || !data.hcpSignature) {
        onError("Missing required data for California submission");
        return;
      }

      // Upload HCP signature to S3
      const hcpSignatureBlob = await base64ToBlob(data.hcpSignature);
      const hcpTimecard = await uploadToS3({
        shiftId: shift._id,
        type: hcpSignatureBlob.type,
        fileBlob: hcpSignatureBlob,
      });

      await submitCaliforniaTimesheet({
        clockInOut: {
          start: data.clockInTime.toISOString(),
          end: data.clockOutTime.toISOString(),
        },
        lunchInOut:
          data.breakStartTime && data.breakEndTime
            ? {
                start: data.breakStartTime.toISOString(),
                end: data.breakEndTime.toISOString(),
              }
            : undefined,
        shiftId: shift._id,
        facilityEmployeeName: data.facilityEmployeeName,
        hcpName: data.hcpName ?? "",
        stationWingUnitFloor: data.workerStation,
        nursingServiceAssignment: data.workerNursingAssignment,
        hcpTimecard,
        mandatoryBreakDurationInMinutes,
        breakAuthorizationReason: data.breakAuthorizationReason,
        missedPunches: data.missedPunches,
        actualClockInOut: isDefined(actualClockInOut)
          ? { start: actualClockInOut.start, end: actualClockInOut.end }
          : undefined,
      });
    },
    [
      shift,
      uploadToS3,
      submitCaliforniaTimesheet,
      onError,
      mandatoryBreakDurationInMinutes,
      actualClockInOut,
    ]
  );

  const submitTimesheetWithoutSignature = useCallback(
    async (data: SubmitTimesheetFormValues) => {
      if (!isDefined(shift?._id)) {
        onError("Missing required data for submission");
        return;
      }

      await submitTimesheet({
        clockInOut: {
          start: data.clockInTime.toISOString(),
          end: data.clockOutTime.toISOString(),
        },
        lunchInOut:
          data.breakStartTime && data.breakEndTime
            ? {
                start: data.breakStartTime.toISOString(),
                end: data.breakEndTime.toISOString(),
              }
            : undefined,
        shiftId: shift._id,
        mandatoryBreakDurationInMinutes,
        breakAuthorizationReason: data.breakAuthorizationReason,
        missedPunches: data.missedPunches,
        actualClockInOut: isDefined(actualClockInOut)
          ? { start: actualClockInOut.start, end: actualClockInOut.end }
          : undefined,
      });
    },
    [shift, submitTimesheet, mandatoryBreakDurationInMinutes, actualClockInOut, onError]
  );

  const handleSubmit = useCallback(
    async (data: SubmitTimesheetFormValues) => {
      try {
        if (isCaliforniaFacility) {
          const submitFunction = isSkipWorkplaceSignatureFlow
            ? submitCaliforniaTimecardWithoutHCFSignature
            : submitCaliforniaTimecard;
          await submitFunction(data);
        } else {
          const submitFunction = isSkipWorkplaceSignatureFlow
            ? submitTimesheetWithoutSignature
            : submitRegularTimecard;
          await submitFunction(data);
        }

        onSuccess();
      } catch (error: unknown) {
        let userMessage = "Something went wrong while submitting your timesheet. Please try again.";

        const apiMessage = getApiErrorMessage(error);
        if (apiMessage) {
          userMessage = apiMessage;
        }

        onError(userMessage);
      }
    },
    [
      isSkipWorkplaceSignatureFlow,
      submitTimesheetWithoutSignature,
      submitCaliforniaTimecardWithoutHCFSignature,
      isCaliforniaFacility,
      submitCaliforniaTimecard,
      submitRegularTimecard,
      onSuccess,
      onError,
    ]
  );

  return {
    handleSubmit,
    submitCaliforniaTimecardWithoutHCFSignature,
    isLoading: isUploading || isSubmitting || isCaliforniaSubmitting,
    formSchema,
  };
}

function getApiErrorMessage(error: unknown): string | undefined {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined;
    const message = data?.message;

    if (typeof message === "string" && message.trim().length > 0) {
      return message;
    }
  }

  return undefined;
}
/* eslint-enable max-lines */
